import AppsIcon from "@mui/icons-material/Apps";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLink } from "@/constants/groupConfig";
import { HomeFolder } from "@/models/HomeLayoutSettings";

interface Props {
  /** Every page this reader may open, by the file name it is published under. */
  pagesByUrl: ReadonlyMap<string, PageLink>;
  folders: HomeFolder[];
  favoriteUrls: string[];
  onOpen: (page: PageLink) => void;
}

/**
 * The nine dots, top left of every page.
 *
 * Every page of this add-in is its own entry point, so there is no navigation of its own to hang
 * this on; it works because all the pages run inside the home page, which swaps between them.
 * That is also why it can sit in the header of whatever page is showing.
 *
 * The order is the reader's own — their favourites first, then their folders as they arranged
 * them — so the menu and the home page cannot tell them different stories about where a page is.
 */
export function AppMenuButton({ pagesByUrl, folders, favoriteUrls, onOpen }: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open = (page: PageLink) => {
    setAnchor(null);
    onOpen(page);
  };

  const renderPages = (urls: readonly string[]) =>
    urls.flatMap(url => {
      const page = pagesByUrl.get(url);
      if (!page) {
        return [];
      }

      return [
        <MenuItem key={url} onClick={() => open(page)}>
          <ListItemIcon sx={{ color: "text.secondary" }}>{page.icon}</ListItemIcon>
          <ListItemText>{page.name}</ListItemText>
        </MenuItem>,
      ];
    });

  const favorites = renderPages(favoriteUrls);

  return (
    <>
      <Tooltip title={t("home.allPages", "Alle Seiten")}>
        <IconButton
          onClick={event => setAnchor(event.currentTarget)}
          aria-label={t("home.allPages", "Alle Seiten")}
          sx={{ color: "inherit" }}
        >
          <AppsIcon />
        </IconButton>
      </Tooltip>

      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        // Tall enough to be worth scrolling rather than paging through.
        slotProps={{ paper: { sx: { maxHeight: "80vh", minWidth: 260 } } }}
      >
        {favorites.length > 0 ? (
          <Box>
            <ListSubheader sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
              {t("home.favorites", "Favoriten")}
            </ListSubheader>
            {favorites}
            <Divider />
          </Box>
        ) : null}

        {folders.flatMap(folder => {
          const pages = renderPages(folder.pageUrls);
          if (pages.length === 0) {
            return [];
          }

          return [
            <Box key={folder.id}>
              <ListSubheader>{folder.name}</ListSubheader>
              {pages}
            </Box>,
          ];
        })}
      </Menu>
    </>
  );
}
